// SarLab API Service
// This module connects the frontend to the SarLab backend
// Token management is handled via HttpOnly cookies set by the server

const API_BASE_URL = process.env.REACT_APP_API_URL;
const TENANT_CODE = process.env.REACT_APP_TENANT_CODE;

// API request helper - uses cookies for authentication
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string; code?: string }> {
  const url = `${API_BASE_URL}/api/public${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add tenant code header for multi-tenant support
  if (TENANT_CODE) {
    headers['X-Tenant'] = TENANT_CODE;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Send cookies with requests
    });

    const data = await response.json();

    if (!response.ok) {
      // If token expired, try to refresh
      if (response.status === 401 && data.code === 'TOKEN_EXPIRED') {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Retry the request with refreshed cookie
          return apiRequest(endpoint, options);
        }
      }
      return { success: false, error: data.error, code: data.code };
    }

    return { success: true, data };
  } catch (error) {
    console.error('API request error:', error);
    return { success: false, error: 'Network error', code: 'NETWORK_ERROR' };
  }
}

// Refresh token using HttpOnly cookie
async function refreshAccessToken(): Promise<boolean> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (TENANT_CODE) {
    headers['X-Tenant'] = TENANT_CODE;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/public/auth/refresh`, {
      method: 'POST',
      headers,
      credentials: 'include', // Send refresh token cookie
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.success === true;
  } catch {
    return false;
  }
}

// ============================================
// AUTH API
// ============================================

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  tcKimlikNo: string;
  phone: string;
  dateOfBirth: string;
  referralCode?: string;
}

export interface LoginData {
  emailOrUsername: string;
  password: string;
}

export interface Player {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  tcKimlikNo?: string;
  status: 'active' | 'suspended' | 'banned';
  kycStatus: 'none' | 'pending' | 'verified' | 'rejected';
  vipLevel: number;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  createdAt?: string;
}

export interface PlayerProfile {
  country?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  occupation?: string;
  currency?: string;
  kycInfoSubmittedAt?: string;
  kycInfoVerifiedAt?: string;
}

export interface KycDocument {
  id: string;
  documentType: string;
  fileName: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  createdAt: string;
}

export interface Wallet {
  balance: number;
  currency: string;
}

export interface AuthResponse {
  success: boolean;
  player?: Player;
  wallet?: Wallet;
}

export const authApi = {
  register: async (data: RegisterData) => {
    return apiRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  login: async (data: LoginData) => {
    return apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  logout: async () => {
    return apiRequest('/auth/logout', { method: 'POST' });
  },

  me: async () => {
    return apiRequest<AuthResponse>('/auth/me');
  },

  // Check if user might be logged in (has session cookie - actual validation done server-side)
  checkSession: async (): Promise<boolean> => {
    const result = await apiRequest<AuthResponse>('/auth/me');
    return result.success && !!result.data?.player;
  },
};

// ============================================
// PROMOTIONS API
// ============================================

export interface Promotion {
  id: string;
  title: string;
  slug: string;
  shortDescription?: string;
  description?: string;
  imageUrl?: string;
  type: 'deposit' | 'cashback' | 'freespin' | 'reload' | 'welcome' | 'vip' | 'custom';
  value?: number;
  valueType: 'fixed' | 'percentage';
  minDeposit?: number;
  maxBonus?: number;
  wagerMultiplier?: number;
  validFrom?: string;
  validTo?: string;
  isFeatured: boolean;
  terms?: string;
}

export interface PromotionsResponse {
  success: boolean;
  promotions: Promotion[];
  total: number;
}

export const promotionsApi = {
  list: async (options?: { featured?: boolean; type?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.featured) params.append('featured', 'true');
    if (options?.type) params.append('type', options.type);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<PromotionsResponse>(`/promotions${query}`);
  },

  getBySlug: async (slug: string) => {
    return apiRequest<{ success: boolean; promotion: Promotion }>(`/promotions/${slug}`);
  },
};

// ============================================
// TOURNAMENTS API
// ============================================

export interface Tournament {
  id: string;
  name: string;
  slug: string;
  description?: string;
  imageUrl?: string;
  bannerUrl?: string;
  prizePool: number;
  prizeDistribution?: Record<string, number>;
  entryFee: number;
  minLevel: number;
  maxParticipants?: number;
  currentParticipants: number;
  startDate: string;
  endDate: string;
  status: 'draft' | 'upcoming' | 'active' | 'finished' | 'cancelled';
  isFeatured: boolean;
}

export interface TournamentsResponse {
  success: boolean;
  tournaments: Tournament[];
  total: number;
}

export const tournamentsApi = {
  list: async (options?: { featured?: boolean; status?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.featured) params.append('featured', 'true');
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<TournamentsResponse>(`/tournaments${query}`);
  },

  getBySlug: async (slug: string) => {
    return apiRequest<{ success: boolean; tournament: Tournament }>(`/tournaments/${slug}`);
  },

  getLeaderboard: async (slug: string) => {
    return apiRequest<{
      success: boolean;
      leaderboard: Array<{
        rank: number;
        playerId: string;
        username: string;
        score: number;
        prize?: number;
      }>;
    }>(`/tournaments/${slug}/leaderboard`);
  },

  join: async (slug: string) => {
    return apiRequest<{ success: boolean }>(`/tournaments/${slug}/join`, { method: 'POST' });
  },
};

// ============================================
// BANNERS API
// ============================================

export interface Banner {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl?: string;
  position: string;
}

export interface BannersResponse {
  success: boolean;
  banners: Banner[];
}

export const bannersApi = {
  list: async (position?: string) => {
    const query = position ? `?position=${position}` : '';
    return apiRequest<BannersResponse>(`/banners${query}`);
  },
};

// ============================================
// GAMES API
// ============================================

export interface Game {
  id: string;
  externalId: string;
  name: string;
  provider: string;
  category: string;
  thumbnailUrl?: string;
  isLive: boolean;
  isNew: boolean;
  isFeatured: boolean;
  rtp?: number;
}

export interface GamesResponse {
  success: boolean;
  games: Game[];
  total: number;
  page: number;
  pageSize: number;
}

export const gamesApi = {
  list: async (options?: {
    category?: string;
    provider?: string;
    search?: string;
    featured?: boolean;
    isNew?: boolean;
    page?: number;
    pageSize?: number;
  }) => {
    const params = new URLSearchParams();
    if (options?.category) params.append('category', options.category);
    if (options?.provider) params.append('provider', options.provider);
    if (options?.search) params.append('search', options.search);
    if (options?.featured) params.append('featured', 'true');
    if (options?.isNew) params.append('isNew', 'true');
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<GamesResponse>(`/games${query}`);
  },

  getById: async (id: string) => {
    return apiRequest<{ success: boolean; game: Game }>(`/games/${id}`);
  },
};

// ============================================
// WALLET API
// ============================================

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'bet' | 'win' | 'bonus' | 'adjustment';
  amount: number;
  balanceAfter: number;
  description?: string;
  createdAt: string;
}

export const walletApi = {
  getBalance: async () => {
    return apiRequest<{ success: boolean; wallet: Wallet }>('/player/wallet');
  },

  getTransactions: async (options?: { type?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.type) params.append('type', options.type);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<{ success: boolean; transactions: Transaction[]; total: number }>(
      `/player/transactions${query}`
    );
  },
};

// ============================================
// CHAT API
// ============================================

export interface ChatMessage {
  id: string;
  playerId: string;
  playerUsername: string;
  content: string;
  createdAt: string;
}

export const chatApi = {
  getMessages: async (limit = 50) => {
    return apiRequest<{ success: boolean; messages: ChatMessage[] }>(
      `/chat/messages?limit=${limit}`
    );
  },

  sendMessage: async (content: string) => {
    return apiRequest<{ success: boolean; message: ChatMessage }>('/chat/messages', {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },

  getStatus: async () => {
    return apiRequest<{ success: boolean; online: number }>('/chat/status');
  },
};

// ============================================
// SUPPORT API
// ============================================

export interface SupportTicket {
  id: string;
  subject: string;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessage {
  id: string;
  ticketId: string;
  senderId: string;
  senderType: 'player' | 'admin';
  content: string;
  createdAt: string;
}

export const supportApi = {
  getTickets: async () => {
    return apiRequest<{ success: boolean; tickets: SupportTicket[] }>('/support/tickets');
  },

  createTicket: async (subject: string, message: string, priority = 'normal') => {
    return apiRequest<{ success: boolean; ticket: SupportTicket }>('/support/tickets', {
      method: 'POST',
      body: JSON.stringify({ subject, message, priority }),
    });
  },

  getTicketMessages: async (ticketId: string) => {
    return apiRequest<{ success: boolean; messages: SupportMessage[] }>(
      `/support/tickets/${ticketId}/messages`
    );
  },

  sendTicketMessage: async (ticketId: string, content: string) => {
    return apiRequest<{ success: boolean; message: SupportMessage }>(
      `/support/tickets/${ticketId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ content }),
      }
    );
  },
};

// ============================================
// PROFILE API
// ============================================

export interface FullProfileResponse {
  success: boolean;
  player: Player;
  profile: PlayerProfile | null;
  documents: KycDocument[];
}

export const profileApi = {
  getFullProfile: async () => {
    return apiRequest<FullProfileResponse>('/player/profile');
  },

  updateKycInfo: async (data: {
    country?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    occupation?: string;
  }) => {
    return apiRequest<{ success: boolean; message: string }>('/player/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getKycDocuments: async () => {
    return apiRequest<{ success: boolean; documents: KycDocument[] }>('/player/kyc');
  },

  uploadKycDocument: async (file: File, documentType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    const url = `${API_BASE_URL}/api/public/player/kyc`;

    const headers: Record<string, string> = {};
    if (TENANT_CODE) {
      headers['X-Tenant'] = TENANT_CODE;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: 'Upload failed' };
    }
  },
};

// ============================================
// CONTACT API
// ============================================

export interface ContactData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export const contactApi = {
  send: async (data: ContactData) => {
    return apiRequest<{ success: boolean; message: string }>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// CALL ME API (Beni Ara)
// ============================================

export const callMeApi = {
  request: async () => {
    return apiRequest<{ success: boolean; message: string }>('/player/call-me', {
      method: 'POST',
    });
  },
};

// Export all APIs
const api = {
  auth: authApi,
  promotions: promotionsApi,
  tournaments: tournamentsApi,
  profile: profileApi,
  banners: bannersApi,
  games: gamesApi,
  wallet: walletApi,
  chat: chatApi,
  support: supportApi,
  contact: contactApi,
  callMe: callMeApi,
};

export default api;
